import { i18n } from '@/main'
import { useMeStore } from '@/stores/me'
import type { Contract } from '@/types/contracts'
import { GtmEventType } from '@/types/gtmEvents'
import { buildClaimsDeclarationUrl } from '@/utils/claimsUrl'
import { sendGtmEvent } from '@/utils/gtmEvent'
import type { NavigationActionItem } from '@/utils/navigation'
import { RequestStatus } from '@/utils/restStore'
import { defineStore } from 'pinia'

import assistanceIcon from '@/assets/svg/nav-icons/assistance.svg?component'
import claimIcon from '@/assets/svg/nav-icons/claim_shield.svg?component'

const YAGO_OPERATOR_UTM_SOURCE = 'yago_operator'
const YAGO_CLIENT_UTM_SOURCE = 'yago_client'

interface State {
  currentContractSelected: Contract | null
}

const initialState = (): State => ({
  currentContractSelected: null
})

const gtmEventParams = (name: GtmEventType) => {
  return { name: name, category: 'claim_modal', label: name }
}

const addUtmSource = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    if (!localStorage.getItem('operatorId')) {
      parsedUrl.searchParams.append('utm_source', YAGO_CLIENT_UTM_SOURCE)
    } else {
      parsedUrl.searchParams.append('utm_source', YAGO_OPERATOR_UTM_SOURCE)
    }
    return parsedUrl.toString()
  } catch (error) {
    return url
  }
}

export const useClaimInfoStore = defineStore({
  id: 'claim-info',
  state: initialState,
  getters: {
    claimNavigationItems: ({ currentContractSelected }: State): NavigationActionItem[] => {
      if (!currentContractSelected) return []

      /** @ts-ignore */
      const $t = i18n.global.t
      /** @ts-ignore */
      const locale = i18n.global.locale.value

      const meStore = useMeStore()
      const me = meStore.me.status === RequestStatus.RequestLoaded ? meStore.me.data : null
      const company = meStore.company.status === RequestStatus.RequestLoaded ? meStore.company.data : null
      const profile = meStore.selectedProfile
      const pcaEmail = meStore.selectedProfilePca.email

      const claimItems: NavigationActionItem[] = [
        {
          link: addUtmSource(
            buildClaimsDeclarationUrl({
              contract: currentContractSelected,
              me,
              company,
              profile,
              pcaEmail,
              locale
            })
          ),
          icon: claimIcon,
          title: $t('modals.declare_claim.action_items.register_claim'),
          description: $t('modals.declare_claim.action_items.register_claim_online'),
          callback: () => sendGtmEvent(gtmEventParams(GtmEventType.ClickedOnRegisterClaim))
        }
      ]

      if (currentContractSelected.insurerPhoneNumber) {
        // TODO : additional condition for this (see ticket)
        claimItems.unshift({
          link: `tel:${currentContractSelected.insurerPhoneNumber.replace(/ /g, '')}`,
          icon: assistanceIcon,
          title: $t('modals.declare_claim.action_items.call_assistance'),
          description: currentContractSelected.insurerPhoneNumber,
          callback: () => sendGtmEvent(gtmEventParams(GtmEventType.ClickedOnCallAssistance))
        })
      }

      return claimItems
    }
  }
})
